// ** Action Responses **
// These interfaces define the standardized responses for server actions within the app.
// Each action response communicates both the outcome of the action and any potential errors that occurred.
// Clients utilize these interfaces to effectively handle action results and display pertinent messages to users.

/**
 * Defines the structure of an action response.
 * @template T The type of data returned in the response.
 */
export interface ActionResponse<T = unknown> {
  data?: T // Optional data returned in the response.
  error?: ActionError // Optional error information if an error occurs during the action.
}

export interface ActionResponseSuccess<T> {
  status: 'success'
  code: number
  data: T
}

export interface ActionResponseError {
  status: 'error'
  code: number
  error: ActionError
}

export type _ActionResponse<T> = ActionResponseSuccess<T> | ActionResponseError

/**
 * Defines the structure of an action error.
 */
export interface ActionError {
  name: ActionErrorName // The name of the error.
  message: string // A message describing the error.
}

/**
 * Enumerates the possible error names for actions.
 */
export enum ActionErrorName {
  UserNotFoundError = 'UserNotFoundError', // Indicates that the user was not found.
  EmailAlreadyInUseError = 'EmailAlreadyInUseError', // Indicates that the email is already in use.
  PatientNotFoundError = 'PatientNotFoundError', // Indicates that the patient is already in use.
  JourneyEventAlreadyExistsError = 'JourneyEventAlreadyExistsError', // Indicates that the journey event already exists.
  ServerError = 'ServerError', // Indicates a generic server error.
}
